<template>
  <div class="bg-white border border-gray-200 rounded-lg">
    <!-- Header -->
    <div class="px-4 py-3 bg-primary-50 border-b border-primary-100 rounded-t-lg flex items-center justify-between">
      <h3 class="text-sm font-semibold text-primary-900">
        Performance Summary
        <span v-if="sorted.length" class="text-gray-500 font-normal ml-1">({{ sorted.length }})</span>
      </h3>
      <div class="flex items-center gap-3">
        <div v-if="hasPivotOption" class="flex items-center gap-1.5 text-xs">
          <button
            @click="pivotMode = false"
            class="px-2 py-1 rounded transition-colors"
            :class="!isPivot ? 'bg-primary-200 text-primary-900 font-medium' : 'text-gray-500 hover:text-gray-700'"
          >Single</button>
          <button
            @click="pivotMode = true"
            class="px-2 py-1 rounded transition-colors"
            :class="isPivot ? 'bg-primary-200 text-primary-900 font-medium' : 'text-gray-500 hover:text-gray-700'"
          >Pivot</button>
        </div>

        <input
          v-model="search" data-demo-search
          type="text"
          class="sensitive text-sm w-48 px-3 py-1.5 border border-gray-200 rounded-lg text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-dark"
          placeholder="Search..."
        />
        <ExportMenu :disabled="!sorted.length" :exports="[{ type: 'excel', action: exportTableToExcel }]" />
      </div>
    </div>

    <!-- Dimension Controls -->
    <div v-if="!isDomainOverview" class="px-4 py-2.5 border-b border-gray-200 flex items-center gap-1 flex-wrap">
      <span class="text-xs font-semibold text-gray-600">Entity Type</span>
      <select
        :value="activeEntityKey"
        class="select-chevron text-xs border border-gray-300 rounded-full bg-white pl-2 pr-5 py-0.5 text-gray-500 hover:text-gray-700 hover:border-gray-400 cursor-pointer focus:outline-none transition-colors"
        @change="setEntityType($event.target.value)"
      >
        <option value="">None</option>
        <option v-for="et in filteredEntityTypes" :key="et.type_name" :value="'entity:' + et.type_name">
          {{ et.type_name }}
        </option>
      </select>

      <span class="text-xs font-semibold text-gray-600 ml-2">Dimensions</span>
      <MultiSelectDropdown
        :model-value="nonEntityColumnKeys"
        :options="dimensionDropdownOptions"
        :sensitive-labels="false"
        placeholder="+ Dimension"
        :searchable="true"
        :compact="true"
        :hide-single-value="true"
        max-width="500px"
        @change="onDimensionSelectionChange"
      />
      <Tag
        v-for="col in nonEntityColumns"
        :key="col.key"
        :label="col.label"
        color="neutral"
        :removable="true"
        @remove="removeColumn(col.key)"
      />
      <button
        v-if="nonEntityColumns.length > 1"
        @click="clearAllDimensions"
        class="text-xs text-gray-400 hover:text-gray-600 ml-1"
      >Clear all</button>

      <!-- My Practices -->
      <button
        @click="myPracticesOnly = !myPracticesOnly"
        class="inline-flex items-center gap-2 px-3 py-1 ml-2 rounded border text-xs font-medium transition-colors"
        :class="myPracticesOnly
          ? 'bg-secondary-100 border-secondary-300 text-secondary-900'
          : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'"
      >My Practices</button>

      <!-- View Selector (pivot only) -->
      <div v-if="isPivot" class="relative ml-auto">
        <div
          v-if="showViewMenu"
          class="fixed inset-0 z-10"
          @click="closeViewMenu"
        ></div>
        <button
          @click="showViewMenu = !showViewMenu"
          class="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded-md bg-white hover:bg-gray-50 transition-colors border border-gray-200"
        >
          <span class="text-gray-400">View:</span>
          <span class="text-gray-800">{{ currentViewLabel }}</span>
          <span
            v-if="viewDirty"
            class="text-xs px-1 rounded bg-primary-50 text-primary-500"
          >modified</span>
          <svg class="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/></svg>
        </button>
        <div
          v-if="showViewMenu"
          class="absolute top-full right-0 mt-1 bg-white rounded-md shadow-lg z-20 border border-gray-200"
          style="min-width: 240px;"
        >
          <div v-if="builtinViews.length" class="px-3 py-1.5 text-xs uppercase tracking-wide text-gray-400">Presets</div>
          <button
            v-for="v in builtinViews"
            :key="v.key"
            @click="selectView(v.key)"
            class="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 flex items-center justify-between"
            :class="currentViewKey === v.key ? 'bg-primary-50 text-primary-600 font-semibold' : 'text-gray-800'"
          >
            <span>{{ v.name }}</span>
            <span v-if="currentViewKey === v.key" class="text-xs">&#10003;</span>
          </button>

          <div v-if="userViews.length" class="px-3 py-1.5 text-xs uppercase tracking-wide text-gray-400 mt-1 border-t border-gray-100">My Views</div>
          <div
            v-for="v in userViews"
            :key="v.key"
            class="px-3 py-1.5 text-xs hover:bg-gray-50 flex items-center justify-between cursor-pointer"
            :class="currentViewKey === v.key ? 'bg-primary-50 text-primary-600 font-semibold' : 'text-gray-800'"
            @click="selectView(v.key)"
          >
            <span>{{ v.name }}</span>
            <button
              @click.stop="deleteUserView(v.key)"
              class="text-xs px-1.5 hover:bg-gray-200 rounded text-gray-400"
              title="Delete view"
            >&times;</button>
          </div>

          <div class="px-2 py-2 border-t border-gray-100">
            <div v-if="!showSaveAsInput" class="flex flex-col gap-1">
              <button
                v-if="currentViewKey"
                @click="clearView"
                class="w-full text-left px-2 py-1 text-xs rounded hover:bg-gray-50 text-gray-500"
              >Clear view</button>
              <button
                @click="openSaveAs"
                class="w-full text-left px-2 py-1 text-xs rounded hover:bg-gray-50 text-gray-800"
              >+ Save current as&hellip;</button>
              <button
                v-if="canSaveOverwrite && viewDirty"
                @click="overwriteCurrentView"
                class="w-full text-left px-2 py-1 text-xs rounded hover:bg-gray-50 text-gray-800"
              >Save changes to &ldquo;{{ currentView.name }}&rdquo;</button>
            </div>
            <div v-else class="flex gap-1">
              <input
                v-model="saveAsName"
                @keyup.enter="confirmSaveAs"
                @keyup.escape="cancelSaveAs"
                ref="saveAsInput"
                placeholder="View name"
                class="flex-1 px-2 py-1 text-xs rounded border border-gray-200"
              />
              <button
                @click="confirmSaveAs"
                :disabled="!saveAsName.trim()"
                class="px-2 py-1 text-xs rounded bg-primary-500 text-white"
              >Save</button>
              <button
                @click="cancelSaveAs"
                class="px-2 py-1 text-xs rounded text-gray-500"
              >&times;</button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Filter Controls -->
    <div v-if="!isDomainOverview" class="px-4 py-2 border-b border-gray-200 flex items-center gap-2 flex-wrap">
      <span class="text-xs font-semibold text-gray-600">Filters</span>
      <!-- Committed filter chips (multiple filters are ANDed together) -->
      <Tag
        v-for="chip in committedFilterChips"
        :key="chip.dataKey"
        :class="{ sensitive: chip.dataKey === 'entity_name' }"
        :label="`${chip.label}: ${chip.display}`"
        color="primary"
        :removable="true"
        @remove="clearFilter(chip.dataKey)"
      />
      <!-- Value selector for the filter being added -->
      <span v-if="pendingFilterDim" class="inline-flex items-center gap-1">
        <span class="text-xs text-gray-600 font-medium">{{ pendingFilterLabel }}:</span>
        <MultiSelectDropdown
          :model-value="columnFilters[pendingFilterDim] || []"
          :options="pendingFilterOptions"
          :sensitive-labels="!!pendingFilterEntityType"
          placeholder="Select..."
          :compact="true"
          :searchable="true"
          :hide-single-value="true"
          max-width="500px"
          @update:model-value="setColumnFilter(pendingFilterDim, $event)"
          @close="commitPendingFilter"
        />
        <button
          @click="cancelPendingFilter"
          class="text-gray-400 hover:text-gray-600 text-xs leading-none transition-colors"
          title="Remove"
        >&times;</button>
      </span>
      <!-- Add another filter: pick dimension then multi-select values -->
      <select
        v-if="!pendingFilterDim"
        class="select-chevron text-xs border border-gray-300 rounded-full bg-white pl-2 pr-5 py-0.5 text-gray-500 hover:text-gray-700 hover:border-gray-400 cursor-pointer focus:outline-none transition-colors"
        @change="onFilterDimSelected($event.target.value); $event.target.value = ''"
      >
        <option value="">+ Filter</option>
        <optgroup v-if="menuFilterEntityTypes.length" label="Entity">
          <option v-for="et of menuFilterEntityTypes" :key="'entity_name:' + et" :value="'entity_filter:' + et">{{ et }}</option>
        </optgroup>
        <optgroup v-if="menuContractDims.length" label="Contract">
          <option v-for="c of menuContractDims" :key="c.dataKey" :value="c.dataKey">{{ c.label }}</option>
        </optgroup>
        <optgroup v-if="menuHasTag" label="Tags">
          <option value="infotag_name">Tag</option>
        </optgroup>
      </select>
    </div>

    <!-- Metric Controls (pivot mode only) -->
    <div v-if="isPivot" class="px-4 py-2 border-b border-gray-200 flex items-center gap-1 flex-wrap">
      <span class="text-xs font-semibold text-gray-600">Metrics</span>
      <MultiSelectDropdown
        v-model="selectedMetricIds"
        :options="metricDropdownOptions"
        :sensitive-labels="false"
        placeholder="+ Metric"
        :searchable="true"
        :compact="true"
        :hide-single-value="true"
        max-width="500px"
        @change="onMetricSelectionChange"
      />
      <Tag
        v-for="m in activeMetricLinks"
        :key="m.metric_id"
        :label="m.metric_name"
        color="secondary"
        :removable="true"
        @remove="removeMetric(m.metric_id)"
      />
      <button
        v-if="activeMetricLinks.length > 1"
        @click="clearAllMetrics"
        class="text-xs text-gray-400 hover:text-gray-600 ml-1"
      >Clear all</button>
    </div>

      <LoadingIndicator v-if="loading" message="Loading summary..." />

      <div v-else-if="sorted.length > 0" class="overflow-x-auto max-h-[500px] overflow-y-auto">
        <table class="w-full text-xs">
          <thead class="bg-gray-50 sticky top-0 z-10">
            <tr>
              <th
                v-for="tc in orderedTableColumns"
                :key="tc.id"
                class="px-4 py-2.5 text-xs font-medium text-gray-600 select-none"
                :class="[
                  dragColumnId === tc.id ? 'opacity-50' : '',
                  tc.draggable ? 'cursor-grab' : 'cursor-pointer',
                  tc.type === 'performance' || tc.type === 'dimension' ? 'text-left' : tc.type === 'trend' ? 'text-center' : 'text-right'
                ]"
                :style="tc.type === 'performance' ? 'min-width: 280px' : ''"
                :draggable="tc.draggable"
                @dragstart="tc.draggable && onColumnDragStart(tc.id)"
                @dragover="tc.draggable && onColumnDragOver($event, tc.id)"
                @dragend="onColumnDragEnd"
                @click="doSort(columnSortKey(tc))"
              >
                <span class="inline-flex items-center gap-1" :class="tc.type === 'trend' ? 'justify-center' : tc.type === 'dimension' || tc.type === 'performance' ? '' : 'justify-end'">
                  {{ columnLabel(tc) }}
                  <span v-if="effectiveSortKey === columnSortKey(tc)" class="text-gray-400 text-[10px]">{{ sortAsc ? '\u25B2' : '\u25BC' }}</span>
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, i) in sorted" :key="i" class="border-t border-gray-100 hover:bg-gray-50 transition-colors">
              <td
                v-for="tc in orderedTableColumns"
                :key="tc.id"
                class="px-4"
                :class="cellClass(tc, row)"
              >
                <!-- Dimension cells -->
                <template v-if="tc.type === 'dimension'">
                  <router-link
                    v-if="isDomainOverview && tc.col.key === 'domain' && row.domain_id"
                    :to="`/insight/domain/${row.domain_id}`"
                    class="text-neutral-700 hover:text-neutral-900 hover:underline text-left text-xs font-medium transition-colors"
                  >{{ row[tc.col.dataKey] }}</router-link>
                  <router-link
                    v-else-if="isDomainOverview && tc.col.key === 'metric' && row.metric_id"
                    :to="`/insight/metric/${row.metric_id}`"
                    class="text-neutral-700 hover:text-neutral-900 hover:underline text-left text-xs font-medium transition-colors"
                  >{{ row[tc.col.dataKey] }}</router-link>
                  <button
                    v-else-if="tc.col.category === 'entity'"
                    @click="$emit('drill-entity', row)"
                    class="sensitive text-neutral-700 hover:text-neutral-900 hover:underline text-left text-xs font-medium transition-colors"
                  >{{ row[tc.col.dataKey] }}</button>
                  <span v-else class="text-xs text-gray-600">{{ row[tc.col.dataKey] || '-' }}</span>
                </template>

                <!-- Metric cells (pivot) -->
                <template v-else-if="tc.type === 'metric'">
                  {{ row[tc.metric.dataKey] != null ? formatPivotCell(row[tc.metric.dataKey], tc.metric.id) : '-' }}
                </template>

                <!-- Performance bullet (single) -->
                <template v-else-if="tc.type === 'performance'">
                  <div
                    @mouseenter="showBulletTooltip(row, $event)"
                    @mousemove="moveBulletTooltip($event)"
                    @mouseleave="tooltipRow = null"
                  >
                    <div v-if="row.denominator" class="flex items-center gap-1.5">
                      <span class="text-[10px] text-gray-400 tabular-nums w-7 text-right shrink-0">{{ barMin(row) }}</span>
                      <div class="relative flex-1 h-3.5 bg-gray-100 rounded-sm overflow-hidden" style="min-width: 140px">
                        <div class="absolute inset-y-0 left-0 rounded-sm transition-all" :class="barColor(row)" :style="{ width: barWidthPct(row) + '%' }"></div>
                        <div v-if="row.rate_efctv != null && rowDisplayConfig(row).show_efctv !== false" class="absolute inset-y-0 w-0.5 bg-primary-500 z-10" :style="{ left: barWidthPctFromRate(row, row.rate_efctv) + '%' }"></div>
                        <span class="absolute top-0 h-full flex items-center text-[11px] font-semibold tabular-nums pointer-events-none" :class="valueFitsInBar(row) ? 'text-white' : 'text-neutral-900'" :style="valueLabelStyle(row)">{{ formatRate(row) }}</span>
                      </div>
                      <span class="text-[10px] text-gray-400 tabular-nums w-7 shrink-0">{{ barMax(row) }}</span>
                    </div>
                    <div v-else class="text-gray-400 text-xs">-</div>
                  </div>
                </template>

                <!-- Patients (single) -->
                <template v-else-if="tc.type === 'patients'">
                  {{ row.attribution?.toLocaleString() || '-' }}
                </template>

                <!-- Max Claims Date (single) -->
                <template v-else-if="tc.type === 'observation_date'">
                  {{ formatObsDate(row.most_recent_ov_claim_date || row.observation_date) }}
                </template>

                <!-- Trend (single) -->
                <template v-else-if="tc.type === 'trend'">
                  <span v-if="row.trend_slope != null" :class="trendClass(row.trend_slope)">
                    {{ trendIcon(row.trend_slope) }} {{ formatTrend(row.trend_slope) }}
                  </span>
                  <span v-else class="text-gray-400 text-xs">-</span>
                </template>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-else-if="!loading && !sorted.length" class="flex items-center justify-center py-8 text-gray-500 text-sm">
        No entity data available
      </div>

    <!-- Bullet chart tooltip -->
    <Teleport to="body">
      <div
        v-if="tooltipRow"
        class="fixed z-50 bg-gray-900 text-white text-[11px] leading-relaxed rounded-lg shadow-lg px-3 py-2 pointer-events-none"
        :style="{ top: tooltipPos.y + 'px', left: tooltipPos.x + 'px' }"
      >
        <div class="flex items-center justify-between gap-6">
          <span class="text-gray-400">Rate</span>
          <span class="font-semibold tabular-nums">{{ tooltipRate }}</span>
        </div>
        <div class="flex items-center justify-between gap-6">
          <span class="text-gray-400">Numerator</span>
          <span class="tabular-nums">{{ tooltipRow.numerator?.toLocaleString() }}</span>
        </div>
        <div class="flex items-center justify-between gap-6">
          <span class="text-gray-400">Denominator</span>
          <span class="tabular-nums">{{ tooltipRow.denominator?.toLocaleString() }}</span>
        </div>
        <div v-if="tooltipRow.attribution" class="flex items-center justify-between gap-6">
          <span class="text-gray-400">Patients</span>
          <span class="tabular-nums">{{ tooltipRow.attribution?.toLocaleString() }}</span>
        </div>
        <template v-if="tooltipRow.rate_efctv != null && rowDisplayConfig(tooltipRow).show_efctv !== false">
          <div class="border-t border-gray-700 mt-1.5 pt-1.5 flex items-center justify-between gap-6">
            <span class="text-gray-400">Last Year Rate</span>
            <span class="tabular-nums">{{ (rowDisplayConfig(tooltipRow).format === 'decimal' || rowDisplayConfig(tooltipRow).format === 'number') ? tooltipRow.rate_efctv.toFixed(rowDisplayConfig(tooltipRow).decimal_places ?? 1) : (tooltipRow.rate_efctv * 100).toFixed(rowDisplayConfig(tooltipRow).decimal_places) + '%' }}</span>
          </div>
          <div v-if="tooltipRow.numerator_efctv != null" class="flex items-center justify-between gap-6">
            <span class="text-gray-400">Last Year Numerator</span>
            <span class="tabular-nums">{{ tooltipRow.numerator_efctv.toLocaleString() }}</span>
          </div>
          <div v-if="tooltipRow.denominator_efctv != null" class="flex items-center justify-between gap-6">
            <span class="text-gray-400">Last Year Denominator</span>
            <span class="tabular-nums">{{ tooltipRow.denominator_efctv.toLocaleString() }}</span>
          </div>
        </template>
        <div v-if="tooltipRow.trend_slope != null" class="flex items-center justify-between gap-6">
          <span class="text-gray-400">Trend</span>
          <span class="tabular-nums">{{ formatTrend(tooltipRow.trend_slope) }}</span>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import axios from 'axios';
import { mapGetters } from 'vuex';
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue';
import MultiSelectDropdown from '@/components/widgetcomponents/library/MultiSelectDropdown.vue';
import Tag from '@/components/widgetcomponents/library/Tag.vue';
import ExportMenu from '@/components/widgetcomponents/library/ExportMenu.vue'
import { exportToExcel } from '@/utils/exportArraytoXlsx';
import {
  BUILTIN_PRESETS,
  loadUserViews,
  saveUserViews,
  loadLastViewKey,
  saveLastViewKey,
} from './PerformanceSummary_Views';
import demoProtectedFields from '@/mixins/demoProtectedFields';

// Dimension columns are NOT hardcoded here. They come from /api/entity-types/,
// which reports a `dimension_key` per entity type and labels it with the
// tenant's own name for that level.
//
// This used to be a fixed ['Payer', 'Contract', 'Contract Cohort'] list that
// mirrored, by hand, a list of entity type names in the backend. Diverge
// renamed its levels in July 2026 and the mirror broke: the picker offered
// entity types the engine then returned nothing for (requirement 2234). Nothing
// in this file should name an entity type again.
//
// Legacy `key` values are kept stable per dimension_key so saved views and
// localStorage written before this change still resolve.
const DIMENSION_LEGACY_KEYS = {
  payer_name: 'payer',
  contract_name: 'contract',
  cpy_name: 'cpy',
};

const DEFAULT_DISPLAY = {
  format: 'percentage',
  higher_is_better: true,
  thresholds: [0.4, 0.7],
  decimal_places: 1,
};

// Filterable dimensions that are not tenant-specific. The contract levels are
// appended at runtime from the same entity-type data that drives the columns.
const STATIC_FILTER_DIMS = [
  { dataKey: 'entity_name', label: 'Entity' },
  { dataKey: 'infotag_name', label: 'Tag' },
];

export default {
  name: 'PerformanceSummary',
  components: { LoadingIndicator, MultiSelectDropdown, Tag, ExportMenu },
  mixins: [demoProtectedFields],
  // The table search matches every column, including entity (practice,
  // provider, facility) names, which are tagged. Demo Mode empties it and the
  // tagged input is read-only. It is not saved with views or to storage.
  demoProtectedFields: ['search'],
  emits: ['drill-entity'],
  props: {
    metricIds: { type: Array, default: null },
    performanceDomainIds: { type: Array, default: null },
    year: { type: Number, required: true },
    entityId: { type: Number, default: null },
    isDomain: { type: Boolean, default: false },
    showDomainColumn: { type: Boolean, default: true },
    showMaxClaimsDateColumn: { type: Boolean, default: true },
    // When set, pin the entity grain to this type and ignore/skip the shared
    // `insightsnew_dimensions` preference — used by embedded consumers (alert
    // drilldown) that always want a fixed leaf grain, not whatever the user
    // last picked on the metric page.
    lockEntityType: { type: String, default: null },
  },
  data() {
    const saved = localStorage.getItem('insightsnew_dimensions');
    const savedPivot = localStorage.getItem('performance_summary_pivot_mode');
    return {
      entityTypes: [],
      activeColumnKeys: this.lockEntityType
        ? ['entity:' + this.lockEntityType]
        : (saved ? JSON.parse(saved) : ['entity:Practice', 'payer', 'contract', 'cpy']),
      myPracticesOnly: false,
      myPracticeEntityIds: [],
      myPracticeEntityNames: [],
      infotagTypes: [],

      loading: false,
      rawData: [],
      metricLinks: [],
      metricDisplayConfig: null,
      cachedFilterOptions: {},

      search: '',
      sortKey: null,
      sortAsc: true,
      columnFilters: {},
      pendingFilterDim: null,
      pendingFilterEntityType: null,
      filterEntityType: null,
      entityFilterOptions: [],
      // Contract-dimension filter values fetched on demand (keyed by dataKey),
      // so a dimension can be filtered without first adding it as a column.
      dimValueOptions: {},
      hiddenMetricIds: [],
      columnOrder: [],
      dragColumnId: null,
      tooltipRow: null,
      tooltipPos: { x: 0, y: 0 },
      pivotMode: savedPivot !== null ? JSON.parse(savedPivot) : null,
      domainDisplayConfigs: {},

      // Saved views
      userViews: loadUserViews(),
      currentViewKey: null,
      showViewMenu: false,
      showSaveAsInput: false,
      saveAsName: '',
    };
  },
  computed: {
    ...mapGetters('tenant', ['current_tenant_slug']),
    builtinViews() {
      const slug = this.current_tenant_slug;
      return BUILTIN_PRESETS
        .filter(p => !p.tenants || p.tenants.includes(slug))
        .map(p => ({ ...p, builtin: true }));
    },
    allViews() {
      return [
        ...this.builtinViews,
        ...this.userViews.map(v => ({ ...v, builtin: false })),
      ];
    },
    currentView() {
      return this.allViews.find(v => v.key === this.currentViewKey) || null;
    },
    currentViewLabel() {
      return this.currentView ? this.currentView.name : 'Custom';
    },
    canSaveOverwrite() {
      return !!this.currentView && !this.currentView.builtin;
    },
    viewDirty() {
      if (!this.currentView) return false;
      return JSON.stringify(this._captureCurrentState()) !== JSON.stringify(this.currentView.state);
    },
    hasViews() {
      return this.builtinViews.length > 0 || this.userViews.length > 0;
    },
    metricId() {
      if (this.metricIds && this.metricIds.length === 1) return this.metricIds[0];
      return null;
    },
    hasPivotOption() {
      return this.metricLinks && this.metricLinks.length > 1;
    },
    isDomainOverview() {
      return this.isDomain && !this.isPivot;
    },
    shouldShowDomainColumn() {
      if (this.isDomain && this.performanceDomainIds?.length) return false;
      return this.showDomainColumn;
    },
    isPivot() {
      if (this.pivotMode !== null) return this.pivotMode;
      if (this.isDomain) return false;
      if (this.metricId && this.hasPivotOption) return false;
      return this.hasPivotOption;
    },
    displayConfig() {
      return { ...DEFAULT_DISPLAY, ...(this.metricDisplayConfig || {}) };
    },
    tooltipRate() {
      if (!this.tooltipRow?.denominator) return '-';
      return this.formatValue(this.tooltipRow);
    },
    metricDropdownOptions() {
      if (!this.metricLinks) return [];
      return this.metricLinks.map(l => ({ value: l.metric_id, label: l.metric_name }));
    },
    selectedMetricIds: {
      get() {
        if (!this.metricLinks) return [];
        return this.metricLinks
          .filter(l => !this.hiddenMetricIds.includes(l.metric_id))
          .map(l => l.metric_id);
      },
      set(ids) {
        this.hiddenMetricIds = this.metricLinks
          .filter(l => !ids.includes(l.metric_id))
          .map(l => l.metric_id);
      },
    },
    activeMetricLinks() {
      if (!this.isPivot) return [];
      return this.metricLinks.filter(l => !this.hiddenMetricIds.includes(l.metric_id));
    },
    hiddenMetricLinks() {
      if (!this.isPivot) return [];
      return this.metricLinks.filter(l => this.hiddenMetricIds.includes(l.metric_id));
    },
    pivotMetricColumns() {
      return this.activeMetricLinks.map(l => ({
        id: l.metric_id,
        name: l.metric_name,
        dataKey: 'metric_' + l.metric_id,
      }));
    },
    allTableColumns() {
      const cols = this.activeColumns.map(c => ({
        id: 'dim:' + c.key,
        type: 'dimension',
        col: c,
        draggable: this.isPivot && c.category !== 'entity',
      }));
      if (this.isPivot) {
        for (const m of this.pivotMetricColumns) {
          cols.push({ id: 'metric:' + m.id, type: 'metric', metric: m, draggable: true });
        }
      } else {
        cols.push({ id: 'fixed:performance', type: 'performance', draggable: false });
        cols.push({ id: 'fixed:patients', type: 'patients', draggable: false });
        if (this.showMaxClaimsDateColumn) cols.push({ id: 'fixed:observation_date', type: 'observation_date', draggable: false });
        if (this.hasTrend) cols.push({ id: 'fixed:trend', type: 'trend', draggable: false });
      }
      return cols;
    },
    orderedTableColumns() {
      const fixed = this.allTableColumns.filter(c => !c.draggable);
      const draggable = this.allTableColumns.filter(c => c.draggable);
      if (this.columnOrder.length) {
        const orderMap = new Map(this.columnOrder.map((id, i) => [id, i]));
        draggable.sort((a, b) => {
          const ai = orderMap.has(a.id) ? orderMap.get(a.id) : Infinity;
          const bi = orderMap.has(b.id) ? orderMap.get(b.id) : Infinity;
          return ai - bi;
        });
      }
      return [...fixed, ...draggable];
    },
    // Entity types selectable as a row grain. Filter on `is_selectable_grain`,
    // NOT on `!is_contract_dimension` — there are two ways a type can yield
    // nothing, and the dimension flag only covers one. Diverge's
    // identifier-less "Contract Performance Year" is not a dimension but has no
    // providers beneath it, so it would still return an empty table. The
    // backend computes both and folds them into this one flag.
    filteredEntityTypes() {
      return this.entityTypes.filter(et => et.is_selectable_grain);
    },
    // Dimension columns for this tenant. Use `dimension_label`, not
    // `type_name` — the backend labels the performance-year column for what it
    // actually contains rather than for the entity type behind it.
    // Diverge reads Payer / Contract Pool / Performance Year; Vytalize reads
    // Payer / Contract / Performance Year.
    dimensionColumns() {
      return this.entityTypes
        .filter(et => et.dimension_key)
        .map(et => ({
          key: DIMENSION_LEGACY_KEYS[et.dimension_key] || et.dimension_key,
          label: et.dimension_label || et.type_name,
          dataKey: et.dimension_key,
          entityTypeName: et.type_name,
        }));
    },
    allColumnOptions() {
      const options = [];
      for (const et of this.filteredEntityTypes) {
        options.push({ key: 'entity:' + et.type_name, label: et.type_name, dataKey: 'entity_name', category: 'entity', entityType: et.type_name });
      }
      options.push(...this.dimensionColumns.map(c => ({ ...c, category: 'contract' })));
      for (const t of this.infotagTypes) {
        options.push({ key: 'infotag_' + t.id, label: t.name, dataKey: 'infotag_name', category: 'infotag', infotagTypeId: t.id });
      }
      return options;
    },
    activeColumns() {
      if (this.isDomainOverview) {
        const cols = [];
        if (this.shouldShowDomainColumn) cols.push({ key: 'domain', label: 'Domain', dataKey: 'entity_name', category: 'entity' });
        return [
          ...cols,
          { key: 'metric', label: 'Metric', dataKey: 'metric_name', category: 'label' },
        ];
      }
      return this.activeColumnKeys
        .map(k => this.allColumnOptions.find(c => c.key === k))
        .filter(Boolean);
    },
    hasEntityColumn() {
      return this.activeColumns.some(c => c.category === 'entity');
    },
    availableColumns() {
      const used = new Set(this.activeColumnKeys);
      return this.allColumnOptions.filter(c => {
        if (used.has(c.key)) return false;
        if (c.category === 'entity' && this.hasEntityColumn) return false;
        return true;
      });
    },
    availableEntityTypes() {
      return this.availableColumns.filter(c => c.category === 'entity');
    },
    availableContractCols() {
      return this.availableColumns.filter(c => c.category === 'contract');
    },
    availableInfotagCols() {
      return this.availableColumns.filter(c => c.category === 'infotag');
    },
    activeEntityKey() {
      return this.activeColumnKeys.find(k => k.startsWith('entity:')) || '';
    },
    nonEntityColumnKeys() {
      return this.activeColumnKeys.filter(k => !k.startsWith('entity:'));
    },
    nonEntityColumns() {
      return this.activeColumns.filter(c => c.category !== 'entity');
    },
    dimensionDropdownOptions() {
      const groupMap = { contract: 'Contract', infotag: 'Tags' };
      return this.allColumnOptions
        .filter(c => c.category !== 'entity')
        .map(c => ({
          value: c.key,
          label: c.label,
          group: groupMap[c.category] || 'Other',
        }));
    },
    // Derived API params from active dimensions
    activeEntityType() {
      const col = this.activeColumns.find(c => c.category === 'entity');
      return col?.entityType || null;
    },
    activeInfotagTypeId() {
      const col = this.activeColumns.find(c => c.category === 'infotag');
      return col?.infotagTypeId || null;
    },
    activeDimensions() {
      return this.activeColumns.filter(c => c.category === 'contract').map(c => c.dataKey);
    },
    // Non-empty filters for API
    activeFilters() {
      const f = {};
      for (const [k, v] of Object.entries(this.columnFilters)) {
        if (v && v.length > 0) f[k] = v;
      }
      return Object.keys(f).length > 0 ? f : null;
    },
    // Filter UI
    filterEntityTypes() {
      return this.filteredEntityTypes.map(et => et.type_name);
    },
    // Every filterable dimension: entity + this tenant's contract levels + tags.
    allFilterDims() {
      return [
        STATIC_FILTER_DIMS[0],
        ...this.dimensionColumns.map(c => ({ dataKey: c.dataKey, label: c.label })),
        STATIC_FILTER_DIMS[1],
      ];
    },
    columnFilterOptions() {
      return this.cachedFilterOptions;
    },
    activeFilterChips() {
      const chips = [];
      for (const [dataKey, vals] of Object.entries(this.columnFilters)) {
        if (!vals || vals.length === 0) continue;
        let label;
        let displayVals = vals;
        if (dataKey === 'entity_name' && this.filterEntityType) {
          label = this.filterEntityType;
          const idToLabel = Object.fromEntries(this.entityFilterOptions.map(o => [o.value, o.label]));
          displayVals = vals.map(v => idToLabel[v] || v);
        } else {
          const dim = this.allFilterDims.find(d => d.dataKey === dataKey);
          label = dim?.label || dataKey;
        }
        const display = displayVals.length <= 2 ? displayVals.join(', ') : `${displayVals[0]} +${displayVals.length - 1}`;
        chips.push({ dataKey, label, display });
      }
      return chips;
    },
    availableFilterDims() {
      return this.allFilterDims.filter(d =>
        this.columnFilterOptions[d.dataKey]?.length > 0
      );
    },
    // Committed filters shown as chips. The one currently being added lives in
    // the value selector instead, so it is excluded here to avoid a duplicate.
    committedFilterChips() {
      return this.activeFilterChips.filter(c => c.dataKey !== this.pendingFilterDim);
    },
    // Entity types offered in "+ Filter". Only one entity filter is supported —
    // it occupies the single entity_name slot and the backend ORs (not ANDs)
    // multiple entity_ids — so hide entity options once one is active.
    menuFilterEntityTypes() {
      return this.columnFilters['entity_name'] ? [] : this.filterEntityTypes;
    },
    // Contract dimensions offered in "+ Filter", excluding ones already active.
    menuContractDims() {
      return this.dimensionColumns.filter(c => !this.columnFilters[c.dataKey]);
    },
    // Offer the Tag filter only when tags exist and none is active yet.
    menuHasTag() {
      return !!this.columnFilterOptions['infotag_name'] && !this.columnFilters['infotag_name'];
    },
    pendingFilterLabel() {
      if (this.pendingFilterEntityType) return this.pendingFilterEntityType;
      const dim = this.allFilterDims.find(d => d.dataKey === this.pendingFilterDim);
      return dim?.label || '';
    },
    pendingFilterOptions() {
      if (this.pendingFilterEntityType) return this.entityFilterOptions;
      const dim = this.pendingFilterDim;
      // Prefer values actually present in the loaded rows; fall back to values
      // fetched from the dimension's entities (so a dimension can be filtered
      // without first adding it as a column).
      const scraped = this.columnFilterOptions[dim];
      const vals = (scraped && scraped.length) ? scraped : (this.dimValueOptions[dim] || []);
      return vals.map(v => ({ value: v, label: v }));
    },
    // Display pipeline: rawData → displayRows (pivot reshape) → filtered → sorted
    displayRows() {
      if (!this.isPivot) return this.rawData;
      // Pivot: API returns one row per entity × metric. Reshape to one row per
      // entity with metric rates as columns (metric_1, metric_7, etc.).
      const map = {};
      for (const row of this.rawData) {
        // Key by all non-metric columns (entity + dims)
        const key = [row.entity_id, row.payer_name, row.contract_name, row.cpy_name, row.infotag_name]
          .map(v => v ?? '').join('|');
        if (!map[key]) {
          map[key] = {
            entity_id: row.entity_id,
            entity_name: row.entity_name,
            entity_type: row.entity_type,
            payer_name: row.payer_name,
            contract_name: row.contract_name,
            cpy_name: row.cpy_name,
            infotag_name: row.infotag_name,
          };
        }
        map[key]['metric_' + row.metric_id] = row.rate;
      }
      return Object.values(map);
    },
    hasTrend() {
      return this.rawData.some(r => r.trend_slope != null);
    },
    hasEfctv() {
      return this.displayConfig.show_efctv !== false && this.rawData.some(r => r.rate_efctv != null);
    },
    filtered() {
      if (!this.search) return this.displayRows;
      const s = this.search.toLowerCase();
      return this.displayRows.filter(r =>
        this.activeColumns.some(col => r[col.dataKey] && String(r[col.dataKey]).toLowerCase().includes(s))
      );
    },
    effectiveSortKey() {
      if (this.sortKey) return this.sortKey;
      // Default: sort by first active column
      return this.activeColumns.length > 0 ? this.activeColumns[0].dataKey : 'entity_name';
    },
    sorted() {
      const rows = [...this.filtered];
      const key = this.effectiveSortKey;
      rows.sort((a, b) => {
        const av = a[key] ?? '';
        const bv = b[key] ?? '';
        return this.sortAsc ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1);
      });
      return rows;
    },
    globalRateRange() {
      const rates = this.displayRows.filter(r => r.denominator > 0).map(r => r.numerator / r.denominator);
      if (!rates.length) return { min: 0, max: 1 };
      return { min: 0, max: Math.max(...rates) * 1.1 };
    },
    globalCountRange() {
      const vals = this.displayRows.filter(r => r.numerator > 0).map(r => r.numerator);
      if (!vals.length) return { min: 0, max: 1 };
      return { min: 0, max: Math.max(...vals) * 1.1 };
    },
  },
  watch: {
    metricIds() { this.resolveMetrics(); },
    performanceDomainIds() { this.resolveMetrics(); },
    isPivot() { this.fetchData(); },
    year() { this.fetchData(); },
    entityId() { this.fetchData(); },
    myPracticesOnly() { this.fetchData(); },
    activeColumnKeys: {
      handler(val) {
        if (this.lockEntityType) return;
        if (this._suppressPersist) return;
        localStorage.setItem('insightsnew_dimensions', JSON.stringify(val));
      },
      deep: true,
    },
    pivotMode(val) {
      if (val !== null) {
        localStorage.setItem('performance_summary_pivot_mode', JSON.stringify(val));
      } else {
        localStorage.removeItem('performance_summary_pivot_mode');
      }
    },
  },
  methods: {
    onFilterDimSelected(value) {
      if (value.startsWith('entity_filter:')) {
        const entityType = value.replace('entity_filter:', '');
        this.pendingFilterDim = 'entity_name';
        this.pendingFilterEntityType = entityType;
        this.fetchEntityFilterOptions(entityType);
      } else {
        this.pendingFilterDim = value;
        this.pendingFilterEntityType = null;
        this.fetchDimValueOptions(value);
      }
    },
    // Load selectable values for a contract-dimension filter from that
    // dimension's entities, so it can be filtered without first being added as
    // a column. cpy_name is the performance-year identifier value (a bare year),
    // not an entity name, so it is left to the row-scraped options.
    async fetchDimValueOptions(dataKey) {
      if (dataKey === 'cpy_name' || this.dimValueOptions[dataKey]) return;
      const col = this.dimensionColumns.find(c => c.dataKey === dataKey);
      if (!col || !col.entityTypeName) return;
      try {
        const response = await axios.get('api/entities/', { params: { entity_type_name: col.entityTypeName } });
        const entities = response.data.results || response.data;
        const names = [...new Set(entities.map(e => e.name).filter(Boolean))].sort();
        this.dimValueOptions = { ...this.dimValueOptions, [dataKey]: names };
      } catch (error) {
        console.error('Error fetching dimension filter values:', error);
      }
    },
    async fetchEntityFilterOptions(entityType) {
      try {
        const response = await axios.get('api/entities/', { params: { entity_type_name: entityType } });
        const entities = response.data.results || response.data;
        this.entityFilterOptions = entities.map(e => ({ value: e.id, label: e.name }));
      } catch (error) {
        console.error('Error fetching entities for filter:', error);
        this.entityFilterOptions = [];
      }
    },
    setColumnFilter(dataKey, vals) {
      if (vals && vals.length > 0) {
        this.columnFilters = { ...this.columnFilters, [dataKey]: vals };
        if (dataKey === 'entity_name') this.filterEntityType = this.pendingFilterEntityType;
      } else {
        const copy = { ...this.columnFilters };
        delete copy[dataKey];
        this.columnFilters = copy;
        if (dataKey === 'entity_name') this.filterEntityType = null;
      }
      this.fetchData();
    },
    clearFilter(dataKey) {
      const copy = { ...this.columnFilters };
      delete copy[dataKey];
      this.columnFilters = copy;
      if (dataKey === 'entity_name') this.filterEntityType = null;
      this.fetchData();
    },
    // Collapse the value selector, keeping the filter (already applied) as a
    // chip and re-showing "+ Filter" so another can be added.
    commitPendingFilter() {
      const dim = this.pendingFilterDim;
      this.pendingFilterDim = null;
      this.pendingFilterEntityType = null;
      if (dim === 'entity_name' && !this.columnFilters['entity_name']?.length) {
        this.filterEntityType = null;
      }
    },
    // Collapse the value selector, discarding the in-progress filter.
    cancelPendingFilter() {
      const dim = this.pendingFilterDim;
      this.pendingFilterDim = null;
      this.pendingFilterEntityType = null;
      if (dim && this.columnFilters[dim]) {
        this.clearFilter(dim);
      } else if (dim === 'entity_name') {
        this.filterEntityType = null;
      }
    },
    onMetricSelectionChange(ids) {
      this.selectedMetricIds = ids;
      this.fetchData();
    },
    removeMetric(id) {
      this.hiddenMetricIds.push(id);
      this.fetchData();
    },
    columnLabel(tc) {
      if (tc.type === 'dimension') return tc.col.label;
      if (tc.type === 'metric') return tc.metric.name;
      if (tc.type === 'performance') return 'Performance';
      if (tc.type === 'patients') return 'Patients';
      if (tc.type === 'observation_date') return 'Max Claims Date';
      if (tc.type === 'trend') return 'Trend';
      return '';
    },
    columnSortKey(tc) {
      if (tc.type === 'dimension') return tc.col.dataKey;
      if (tc.type === 'metric') return tc.metric.dataKey;
      if (tc.type === 'performance') return 'rate';
      if (tc.type === 'patients') return 'attribution';
      if (tc.type === 'observation_date') return 'most_recent_ov_claim_date';
      if (tc.type === 'trend') return 'trend_slope';
      return '';
    },
    cellClass(tc, row) {
      if (tc.type === 'dimension') return 'py-2.5';
      if (tc.type === 'metric') return 'py-2.5 text-right text-xs font-medium ' + this.pivotRateColor(row[tc.metric.dataKey]);
      if (tc.type === 'performance') return 'py-2';
      if (tc.type === 'patients') return 'py-2 text-right text-xs text-gray-800 tabular-nums';
      if (tc.type === 'observation_date') return 'py-2 text-right text-xs text-gray-500 tabular-nums whitespace-nowrap';
      if (tc.type === 'trend') return 'py-2 text-center';
      return '';
    },
    onColumnDragStart(columnId) {
      this.dragColumnId = columnId;
    },
    onColumnDragOver(e, targetId) {
      e.preventDefault();
      // Only allow dragging onto other draggable columns
      const target = this.allTableColumns.find(c => c.id === targetId);
      if (!target?.draggable) return;
      if (this.dragColumnId == null || this.dragColumnId === targetId) return;
      const draggable = this.orderedTableColumns.filter(c => c.draggable).map(c => c.id);
      const fromIdx = draggable.indexOf(this.dragColumnId);
      const toIdx = draggable.indexOf(targetId);
      if (fromIdx < 0 || toIdx < 0) return;
      draggable.splice(fromIdx, 1);
      draggable.splice(toIdx, 0, this.dragColumnId);
      this.columnOrder = draggable;
    },
    onColumnDragEnd() {
      this.dragColumnId = null;
    },
    exportTableToExcel() {
      const columns = this.orderedTableColumns;
      const rows = this.sorted.map(row => {
        const out = {};
        for (const tc of columns) {
          const label = this.columnLabel(tc);
          if (tc.type === 'dimension') {
            out[label] = row[tc.col.dataKey] ?? '';
          } else if (tc.type === 'metric') {
            const rate = row[tc.metric.dataKey];
            out[label] = rate != null ? this.formatPivotCell(rate, tc.metric.id) : '-';
          } else if (tc.type === 'performance') {
            out[label] = this.formatValue(row);
          } else if (tc.type === 'patients') {
            out[label] = row.attribution ?? '';
          } else if (tc.type === 'observation_date') {
            out[label] = this.formatObsDate(row.most_recent_ov_claim_date || row.observation_date);
          } else if (tc.type === 'trend') {
            out[label] = row.trend_slope != null ? this.formatTrend(row.trend_slope) : '-';
          }
        }
        return out;
      });
      if (!rows.length) return;
      const filename = this.isPivot ? 'performance_summary_pivot.xlsx' : 'performance_summary.xlsx';
      const sheetName = this.isPivot ? 'Pivot Summary' : 'Summary';
      exportToExcel(rows, filename, { sheetName });
    },
    setEntityType(key) {
      const nonEntity = this.activeColumnKeys.filter(k => !k.startsWith('entity:'));
      this.activeColumnKeys = key ? [key, ...nonEntity] : nonEntity;
      this.sortKey = null;
      this.fetchData();
    },
    onDimensionSelectionChange(keys) {
      // Preserve the current entity key (managed separately)
      const entityKey = this.activeEntityKey;
      this.activeColumnKeys = entityKey ? [entityKey, ...keys] : keys;
      this.sortKey = null;
      clearTimeout(this._dimFetchTimer);
      this._dimFetchTimer = setTimeout(() => this.fetchData(), 500);
    },
    addColumn(key) {
      if (!key) return;
      const opt = this.allColumnOptions.find(o => o.key === key);
      if (!opt) return;
      if (opt.category === 'entity') {
        this.activeColumnKeys = this.activeColumnKeys.filter(k => {
          const c = this.allColumnOptions.find(o => o.key === k);
          return c?.category !== 'entity';
        });
      }
      this.activeColumnKeys.push(key);
      this.sortKey = null;
      this.fetchData();
    },
    removeColumn(key) {
      const idx = this.activeColumnKeys.indexOf(key);
      if (idx >= 0) {
        this.activeColumnKeys.splice(idx, 1);
        this.sortKey = null;
        this.fetchData();
      }
    },
    clearAllDimensions() {
      this.activeColumnKeys = this.activeColumnKeys.filter(k => k.startsWith('entity:'));
      this.sortKey = null;
      this.fetchData();
    },
    clearAllMetrics() {
      this.hiddenMetricIds = this.metricLinks.map(l => l.metric_id);
      this.fetchData();
    },
    async resolveMetrics() {
      this.metricLinks = [];
      this.hiddenMetricIds = [];
      this.metricDisplayConfig = null;
      try {
        if (this.metricIds && this.metricIds.length && !this.isDomain) {
          const responses = await Promise.all(
            this.metricIds.map(id => axios.get(`api/impact_analytics/composable-metrics/${id}/`))
          );
          this.metricLinks = responses.map(r => ({
            metric_id: r.data.id,
            metric_name: r.data.display_name || r.data.name,
          }));
        } else if (this.performanceDomainIds && this.performanceDomainIds.length) {
          const response = await axios.get('api/performancedomains/');
          const domains = response.data.filter(d => this.performanceDomainIds.includes(d.id));
          for (const domain of domains) {
            for (const m of (domain.composable_metrics || [])) {
              this.metricLinks.push({
                metric_id: m.metric_id,
                metric_name: m.metric_name,
                domain_name: domain.name,
                performance_domain: domain.id,
              });
            }
          }
        } else {
          // No metric or domain specified — fetch all domains, pick primaries
          const response = await axios.get('api/performancedomains/');
          for (const domain of response.data) {
            const primary = (domain.composable_metrics || []).find(m => m.primary_metric);
            if (primary) {
              this.metricLinks.push({
                metric_id: primary.metric_id,
                metric_name: primary.metric_name,
                domain_name: domain.name,
                performance_domain: domain.id,
              });
            }
          }
        }
      } catch (error) {
        console.error('Error resolving metrics:', error);
      }
      if (this.metricLinks.length <= 1) this.pivotMode = null;
      this.fetchData();
    },
    buildRequestParams() {
      const entityType = this.activeEntityType || 'Tenant';
      let filters = this.activeFilters;
      if (this.myPracticesOnly && this.myPracticeEntityNames.length) {
        filters = { ...(filters || {}), entity_name: this.myPracticeEntityNames };
      }
      let entityIds = null;
      if (this.filterEntityType && this.filterEntityType !== entityType && filters?.entity_name) {
        entityIds = filters.entity_name;
        const { entity_name, ...rest } = filters;
        filters = Object.keys(rest).length ? rest : null;
      }
      // A contract dimension is only non-NULL in the aggregated output when it
      // is grouped. Force any actively-filtered contract dimension into
      // `dimensions` so its column exists; otherwise the backend filters on a
      // NULL column and silently returns no rows. No-op unless such a filter is
      // active.
      const dimKeys = new Set(this.dimensionColumns.map(c => c.dataKey));
      const dimensions = [...this.activeDimensions];
      for (const k of Object.keys(this.columnFilters)) {
        if (dimKeys.has(k) && !dimensions.includes(k)) dimensions.push(k);
      }
      return {
        entity_type: entityType,
        entity_id: this.entityId,
        entity_ids: entityIds,
        observation_year: this.year,
        infotag_type_id: this.activeInfotagTypeId,
        dimensions: dimensions,
        filters: filters,
      };
    },
    async fetchData() {
      if (!this.isDomainOverview && !this.isPivot && !this.metricId) return;
      if (this.isDomainOverview && (!this.metricLinks || this.metricLinks.length === 0)) return;
      this.loading = true;
      try {
        let response;
        const params = this.buildRequestParams();
        if (this.isDomainOverview) {
          response = await axios.post('api/impact_analytics/composable-metrics/run_multi/', {
            metric_ids: this.metricLinks.map(l => l.metric_id),
            entity_type: 'Tenant',
            group_by_contract: false,
            entity_id: this.entityId,
            observation_year: this.year,
          });
          const metricLinkMap = {};
          for (const link of this.metricLinks) {
            metricLinkMap[link.metric_id] = {
              domain_name: link.domain_name || link.metric_name,
              domain_id: link.performance_domain,
            };
          }
          const results = response.data.results || [];
          for (const row of results) {
            const link = metricLinkMap[row.metric_id] || {};
            row.entity_name = link.domain_name || row.metric_name;
            row.domain_id = link.domain_id;
            row.entity_id = row.metric_id;
            row.entity_type = 'Domain';
          }
          this.rawData = results;
          this.domainDisplayConfigs = {};
          for (const row of results) {
            if (row.display_config) this.domainDisplayConfigs[row.metric_id] = row.display_config;
          }
        } else if (this.isPivot) {
          response = await axios.post('api/impact_analytics/composable-metrics/run_multi/', {
            metric_ids: this.activeMetricLinks.map(l => l.metric_id),
            ...params,
          });
          this.domainDisplayConfigs = {};
          for (const row of (response.data.results || [])) {
            if (row.display_config) this.domainDisplayConfigs[row.metric_id] = row.display_config;
          }
        } else {
          const [dataResp, metaResp] = await Promise.all([
            axios.post(`api/impact_analytics/composable-metrics/${this.metricId}/run/`, {
              time_series: false,
              limit: 500,
              ...params,
            }),
            !this.metricDisplayConfig ? axios.get(`api/impact_analytics/composable-metrics/${this.metricId}/`) : null,
          ]);
          response = dataResp;
          if (metaResp) this.metricDisplayConfig = metaResp.data.display_config || {};
        }
        this.rawData = response.data.results || [];
        // Cache filter options from first unfiltered load
        if (!this.activeFilters) this.buildFilterOptions();
      } catch (error) {
        console.error('Error fetching summary data:', error);
        this.rawData = [];
      } finally {
        this.loading = false;
      }
    },
    buildFilterOptions() {
      const opts = {};
      for (const dim of this.allFilterDims) {
        const vals = new Set();
        for (const row of this.rawData) {
          const v = row[dim.dataKey];
          if (v != null && v !== '') vals.add(v);
        }
        if (vals.size > 0) opts[dim.dataKey] = [...vals].sort();
      }
      this.cachedFilterOptions = opts;
    },
    async fetchEntityTypes() {
      try {
        const response = await axios.get('api/entity-types/');
        this.entityTypes = response.data;
        this.pruneStaleColumnKeys();
      } catch (error) {
        console.error('Error fetching entity types:', error);
      }
    },
    // Drop persisted selections that no longer resolve — e.g. an entity type
    // that has since become a contract dimension. activeColumns already
    // tolerates them, but localStorage would otherwise carry a dead key
    // forever and keep re-saving it.
    pruneStaleColumnKeys() {
      const valid = new Set(this.allColumnOptions.map(c => c.key));
      const kept = this.activeColumnKeys.filter(k => valid.has(k));
      if (kept.length !== this.activeColumnKeys.length) {
        this.activeColumnKeys = kept;
      }
    },
    _applyInitialEntityType() {
      if (this.lockEntityType) return;
      const requestedId = this.$route && this.$route.query ? this.$route.query.entity_type_id : null;
      if (!requestedId) return;
      const match = this.entityTypes.find(
        et => et.is_selectable_grain && String(et.id) === String(requestedId),
      );
      if (!match) return;
      this._suppressPersist = true;
      this.activeColumnKeys = ['entity:' + match.type_name];
      this.currentViewKey = null;
      this.$nextTick(() => { this._suppressPersist = false; });
    },
    async fetchInfotagTypes() {
      try {
        const response = await axios.get('api/infotagtypes/');
        this.infotagTypes = response.data;
      } catch (error) {
        console.error('Error fetching infotag types:', error);
      }
    },
    async fetchMyPracticeEntities() {
      try {
        const response = await axios.get('api/entities/appuser-entity/');
        const entities = response.data.user_entities || response.data;
        this.myPracticeEntityIds = entities.map(e => e.entity_id || e.id);
        this.myPracticeEntityNames = entities.map(e => e.name).filter(Boolean);
      } catch (error) {
        console.error('Error fetching user entities:', error);
        this.myPracticeEntityIds = [];
      }
    },
    doSort(key) {
      if (this.effectiveSortKey === key) { this.sortAsc = !this.sortAsc; }
      else { this.sortKey = key; this.sortAsc = true; }
    },
    rowDisplayConfig(row) {
      if ((this.isDomainOverview || this.isPivot) && row.metric_id && this.domainDisplayConfigs[row.metric_id]) {
        return { ...DEFAULT_DISPLAY, ...this.domainDisplayConfigs[row.metric_id] };
      }
      return this.displayConfig;
    },
    formatValue(row) {
      if (!row.denominator) return '-';
      const dc = this.rowDisplayConfig(row);
      const rate = row.numerator / row.denominator;
      if (dc.format === 'count') return row.numerator.toLocaleString();
      if (dc.format === 'currency') {
        const dp = dc.decimal_places ?? 0;
        return '$' + rate.toLocaleString(undefined, { minimumFractionDigits: dp, maximumFractionDigits: dp });
      }
      if (dc.format === 'decimal' || dc.format === 'number') {
        const dp = dc.decimal_places ?? 1;
        const formatted = rate.toFixed(dp);
        // If rounding hides a nonzero value, show enough digits
        if (formatted === (0).toFixed(dp) && rate > 0) return rate.toPrecision(2);
        return formatted;
      }
      return (rate * 100).toFixed(dc.decimal_places ?? 1) + '%';
    },
    formatRate(row) { return this.formatValue(row); },
    tierColor(rate, type, dc) {
      if (rate == null) return type === 'text' ? 'text-gray-400' : 'bg-gray-200';
      dc = dc || this.displayConfig;
      const [lo, hi] = dc.thresholds;
      const good = dc.higher_is_better ? rate >= hi : rate <= lo;
      const mid = dc.higher_is_better ? rate >= lo : rate <= hi;
      if (type === 'bar') return good ? 'bg-secondary-300' : mid ? 'bg-primary-200' : 'bg-red-light';
      return 'text-neutral-900';
    },
    formatPivotCell(val, metricId) {
      const dc = { ...DEFAULT_DISPLAY, ...(this.domainDisplayConfigs[metricId] || {}) };
      if (dc.format === 'currency') {
        const dp = dc.decimal_places ?? 0;
        return '$' + val.toLocaleString(undefined, { minimumFractionDigits: dp, maximumFractionDigits: dp });
      }
      if (dc.format === 'per_1k') {
        return (val * 1000).toLocaleString(undefined, { maximumFractionDigits: 0 });
      }
      if (dc.format === 'number' || dc.format === 'decimal') {
        const dp = dc.decimal_places ?? 1;
        return val.toFixed(dp);
      }
      return (val * 100).toFixed(dc.decimal_places ?? 1) + '%';
    },
    pivotRateColor(val) { return this.tierColor(val, 'text'); },
    rateColor(row) {
      if (!row.denominator) return 'text-gray-400';
      return this.tierColor(row.numerator / row.denominator, 'text', this.rowDisplayConfig(row));
    },
    barColor() {
      return 'bg-neutral-400';
    },
    barRange(row) {
      const dc = this.rowDisplayConfig(row);
      if (dc.format === 'count') return this.globalCountRange;
      if (dc.format === 'currency' || dc.format === 'decimal') return this.globalRateRange;
      return { min: 0, max: 1 };
    },
    barWidthPct(row) {
      const dc = this.rowDisplayConfig(row);
      const val = dc.format === 'count' ? row.numerator : row.numerator / row.denominator;
      const { min, max } = this.barRange(row);
      return Math.min(Math.max(((val - min) / (max - min)) * 100, 0), 100);
    },
    barWidthPctFromRate(row, rate) {
      const { min, max } = this.barRange(row);
      return Math.min(Math.max(((rate - min) / (max - min)) * 100, 0), 100);
    },
    valueFitsInBar(row) {
      return this.barWidthPct(row) > 35;
    },
    valueLabelStyle(row) {
      const pct = this.barWidthPct(row);
      if (pct > 35) {
        // Inside bar, right-aligned to end of fill
        return { left: '4px', width: (pct - 1) + '%', justifyContent: 'flex-end', paddingRight: '4px' };
      }
      // Outside bar, just after end of fill
      return { left: pct + '%', paddingLeft: '4px' };
    },
    barMin(row) {
      const dc = this.rowDisplayConfig(row);
      const { min } = this.barRange(row);
      if (dc.format === 'count') return min.toLocaleString();
      if (dc.format === 'currency') return '$' + Math.round(min).toLocaleString();
      if (dc.format === 'decimal') return min.toFixed(dc.decimal_places ?? 1);
      return (min * 100).toFixed(0) + '%';
    },
    barMax(row) {
      const dc = this.rowDisplayConfig(row);
      const { max } = this.barRange(row);
      if (dc.format === 'count') return max.toLocaleString();
      if (dc.format === 'currency') return '$' + Math.round(max).toLocaleString();
      if (dc.format === 'decimal') return max.toFixed(dc.decimal_places ?? 1);
      return (max * 100).toFixed(0) + '%';
    },
    formatObsDate(d) {
      if (!d) return '-';
      const dt = new Date(d + 'T00:00:00');
      return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    },
    showBulletTooltip(row, event) {
      this.tooltipRow = row;
      this.tooltipPos = { x: event.pageX + 12, y: event.pageY - 20 };
    },
    moveBulletTooltip(event) {
      this.tooltipPos = { x: event.pageX + 12, y: event.pageY - 20 };
    },
    formatTrend(slope) {
      const monthly = slope * 30;
      const dc = this.displayConfig;
      if (dc.format === 'currency') {
        const val = monthly;
        const sign = val >= 0 ? '+' : '';
        return sign + '$' + Math.round(Math.abs(val)).toLocaleString() + '/mo';
      }
      if (dc.format === 'count') {
        const val = monthly;
        const sign = val >= 0 ? '+' : '-';
        return sign + Math.abs(val).toFixed(1) + '/mo';
      }
      // percentage and decimal: original behavior
      return (monthly * 100).toFixed(1) + '%/mo';
    },
    trendIcon(slope) {
      const m = slope * 30;
      if (m > 0.001) return '\u25B2';
      if (m < -0.001) return '\u25BC';
      return '\u25B8';
    },
    trendClass(slope) {
      const m = slope * 30;
      if (m > 0.001) return 'text-secondary-800 text-xs font-medium';
      if (m < -0.001) return 'text-red-dark text-xs font-medium';
      return 'text-gray-400 text-xs';
    },

    // -- Saved views --
    closeViewMenu() {
      this.showViewMenu = false;
      this.cancelSaveAs();
    },
    clearView() {
      this.currentViewKey = null;
      saveLastViewKey(null);
      this.showViewMenu = false;
    },
    selectView(key) {
      const view = this.allViews.find(v => v.key === key);
      if (!view) return;
      this._applyViewState(view.state);
      this.currentViewKey = key;
      this.showViewMenu = false;
      saveLastViewKey(key);
      this.fetchData();
    },
    _applyViewState(s) {
      const entityKey = s.activeEntityType ? 'entity:' + s.activeEntityType : '';
      const dims = s.activeDimensions || [];
      this.activeColumnKeys = entityKey ? [entityKey, ...dims] : [...dims];
      this.columnFilters = { ...(s.columnFilters || {}) };
      this.hiddenMetricIds = [...(s.hiddenMetricIds || [])];
      this.sortKey = s.sortKey || null;
      this.sortAsc = s.sortAsc ?? true;
    },
    _captureCurrentState() {
      const entityKey = this.activeEntityKey;
      return {
        activeEntityType: entityKey ? entityKey.replace('entity:', '') : null,
        activeDimensions: [...this.nonEntityColumnKeys],
        columnFilters: { ...this.columnFilters },
        hiddenMetricIds: [...this.hiddenMetricIds],
        sortKey: this.sortKey,
        sortAsc: this.sortAsc,
      };
    },
    openSaveAs() {
      this.saveAsName = '';
      this.showSaveAsInput = true;
      this.$nextTick(() => {
        if (this.$refs.saveAsInput) this.$refs.saveAsInput.focus();
      });
    },
    cancelSaveAs() {
      this.showSaveAsInput = false;
      this.saveAsName = '';
    },
    confirmSaveAs() {
      const name = this.saveAsName.trim();
      if (!name) return;
      const key = `user_${Date.now()}`;
      this.userViews.push({ key, name, state: this._captureCurrentState() });
      saveUserViews(this.userViews);
      this.currentViewKey = key;
      saveLastViewKey(key);
      this.cancelSaveAs();
      this.showViewMenu = false;
    },
    overwriteCurrentView() {
      if (!this.canSaveOverwrite) return;
      const idx = this.userViews.findIndex(v => v.key === this.currentViewKey);
      if (idx < 0) return;
      this.userViews[idx] = {
        ...this.userViews[idx],
        state: this._captureCurrentState(),
      };
      saveUserViews(this.userViews);
      this.showViewMenu = false;
    },
    deleteUserView(key) {
      this.userViews = this.userViews.filter(v => v.key !== key);
      saveUserViews(this.userViews);
      if (this.currentViewKey === key) {
        this.currentViewKey = null;
        saveLastViewKey(null);
      }
    },
  },
  async mounted() {
    await this.fetchEntityTypes();
    this.fetchInfotagTypes();
    this.fetchMyPracticeEntities();

    const lastKey = loadLastViewKey();
    const lastView = lastKey ? this.allViews.find(v => v.key === lastKey) : null;
    if (lastView) {
      this._applyViewState(lastView.state);
      this.currentViewKey = lastKey;
    }

    this._applyInitialEntityType();

    await this.resolveMetrics();
  },
};
</script>

<style scoped>
.select-chevron {
  appearance: none;
  background-repeat: no-repeat;
  background-size: 10px;
  background-position: right 6px center;
  background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236b7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'/%3E%3C/svg%3E");
}
</style>
